import React, { useEffect, useMemo } from 'react';

import { FormikValues, useFormik } from 'formik';

import {
  IAllLanguages,
  IGeneral,
  NotificationSettingTypes,
  getEmail,
  getLanguages,
  getUserGeneral,
  language,
  successSelector,
  updateUserGeneral,
  userActions,
  userGeneral,
} from '@pharmaplan/common';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import useLanguage from '../../../../hooks/useLanguge';
import { GeneralValidator } from '../../FormValidation';
import ProfileForm from '../../ProfileForm';
import useDisableProfileSave from '../../../../hooks/useDisableProfileSave';

const handleUndefined = <T, >(
  val: string | string[] | undefined | null,
  expected: T,
): T => {
  if (val === undefined || val === null) return expected;
  return val as T;
};

export interface ICommonProfile {
  setSelected: React.Dispatch<React.SetStateAction<string>>;
}

const General = ({ setSelected }: ICommonProfile) => {
  const dispatch = useAppDispatch();
  const { changeLang } = useLanguage();

  const general = useAppSelector(userGeneral);
  const currentLang = useAppSelector(language);
  const languagesArr = useAppSelector(getLanguages);
  const email = useAppSelector(getEmail);

  const { notificationTypes: notifType, preferredWorkingDays: contactPreferred } = general ?? {};
  const isNotifyIfSelected = !!notifType?.length;

  const generalSaveSuccess = useAppSelector((state) =>
    successSelector([userActions.generalUpdate], state));

  const handleSubmit = (values: FormikValues) => {
    const { notifyIfSelected, preferredWorkingDays } = values ?? {};
    let notificationTypes: Array<NotificationSettingTypes> = [];

    if (notifyIfSelected) {
      notificationTypes = [NotificationSettingTypes.notifyIfSelected];
    }

    dispatch(
      updateUserGeneral({
        ...(values as IGeneral),
        notificationTypes,
        preferredWorkingDays: (preferredWorkingDays as Array<string>)?.join(
          ',',
        ),
      }),
    );
  };

  const initialValue = useMemo(
    () =>
      ({
        ...general,
        email,
        notifyIfSelected: isNotifyIfSelected,
        preferredWorkingDays:
        handleUndefined<Array<string>>(contactPreferred, []).length !== 0
          ? (contactPreferred as string)?.split(',')
          : [],
      }),
    [contactPreferred, email, general, isNotifyIfSelected],
  );

  const formik = useFormik({
    initialValues: initialValue,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validationSchema: GeneralValidator(),
  });

  useEffect(() => {
    if (generalSaveSuccess) {
      const userSelectedLang = languagesArr.filter(
        (lang: IAllLanguages) =>
          lang.languageId === formik?.values.preferdLanguage,
      );

      const isCurrentAndUserLangSame = userSelectedLang[0]?.code === currentLang;

      if (!isCurrentAndUserLangSame) {
        changeLang(userSelectedLang[0]?.code || currentLang);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalSaveSuccess]);

  useEffect(() => {
    dispatch(getUserGeneral());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { disabled } = useDisableProfileSave({
    action: userActions.generalUpdate,
    mutableValues: formik.values,
    initialValue,
  });

  return (
    <ProfileForm
      disableSave={disabled}
      setSelected={setSelected}
      selected="general"
      formik={formik}
    />
  );
};

export default General;
